import { HiOutlineTrash } from 'react-icons/hi2';
import { ColumnDef } from './meta';
import './DataMappings.css';

interface MappingTableProps {
  columns: ColumnDef[];
  rows: Record<string, any>[];
  deletable: boolean;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  onCellChange: (rowIndex: number, field: string, value: unknown) => void;
  onDeleteRow: (rowIndex: number) => void;
}

const numberFmt = (v: unknown): string => {
  const n = Math.round(Number(v) || 0);
  return n.toLocaleString();
};

/** Renders one mapping section's grid from its column config. Deliberately plain markup (no
 * @tanstack/react-table) — sorting is server-side (Hasura `order_by`), so a click-to-sort header
 * only needs to report which field was clicked; the headless table's own row-sorting model would
 * have nothing to do here. */
function MappingTable({ columns, rows, deletable, sortField, sortDir, onSortChange, onCellChange, onDeleteRow }: MappingTableProps) {
  const renderCell = (col: ColumnDef, row: Record<string, any>, rowIndex: number) => {
    const value = row[col.field];

    switch (col.kind) {
      case 'readonly':
        return <span className="dm-cell-ro">{value ?? '—'}</span>;
      case 'readonlyStrong':
        return <span className="dm-cell-ro-strong">{value ?? '—'}</span>;
      case 'readonlyNumber':
        return <span className="dm-cell-ro-num">{numberFmt(value)}</span>;
      case 'select': {
        const options = col.options || [];
        const raw = value == null ? '' : String(value);
        // DB values don't always match an option's casing exactly (e.g. "other" vs "Other"); fall
        // back to a case-insensitive match so the select reflects the stored value rather than
        // silently defaulting to the first option. When nothing matches (empty/unrecognized
        // value), render an explicit placeholder instead of letting the browser default to the
        // first <option> in the list.
        const selected = options.find((opt) => opt.toLowerCase() === raw.toLowerCase());
        return (
          <select
            className="dm-input dm-select"
            value={selected ?? raw}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value)}
          >
            {selected === undefined && <option value={raw}>{raw || '—'}</option>}
            {options.map((opt) => (
              <option key={opt} value={opt}>{col.optionLabels?.[opt] ?? opt}</option>
            ))}
          </select>
        );
      }
      case 'number':
        return (
          <input
            className="dm-input dm-input-num"
            type="number"
            step="1"
            value={value ?? 0}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value === '' ? 0 : Math.round(Number(e.target.value) || 0))}
          />
        );
      case 'decimal':
        return (
          <input
            className="dm-input dm-input-num"
            type="number"
            step="0.01"
            value={value ?? ''}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value === '' ? null : Number(e.target.value))}
          />
        );
      case 'date':
        return (
          <input
            className="dm-input"
            type="date"
            value={value || ''}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value || null)}
          />
        );
      case 'boolean':
        return (
          <select
            className="dm-input dm-select dm-select-bool"
            style={col.minWidth ? { minWidth: col.minWidth } : undefined}
            value={value ? 'yes' : 'no'}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value === 'yes')}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        );
      case 'textarea':
        return (
          <textarea
            className="dm-input dm-textarea"
            style={col.minWidth ? { minWidth: col.minWidth } : undefined}
            rows={2}
            value={value ?? ''}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value)}
          />
        );
      case 'text':
      default:
        return (
          <input
            className="dm-input"
            style={col.minWidth ? { minWidth: col.minWidth } : undefined}
            value={value ?? ''}
            onChange={(e) => onCellChange(rowIndex, col.field, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="dm-table-wrap">
      <table className="dm-table">
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = col.field === sortField;
              return (
                <th
                  key={col.field}
                  className={`dm-th-sortable ${col.kind.startsWith('readonlyNumber') ? 'dm-th-r' : ''}`}
                  onClick={() => onSortChange(col.field)}
                >
                  {col.header}
                  <span className="dm-sort-icon">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}</span>
                </th>
              );
            })}
            <th className="dm-th-narrow" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id ?? `new-${i}`}>
              {columns.map((col) => (
                <td key={col.field}>{renderCell(col, row, i)}</td>
              ))}
              <td className="dm-td-action">
                <div className="dm-action-inner">
                  {row.__dirty && <span className="dm-dirty-dot" />}
                  {deletable && (
                    <button
                      type="button"
                      className="dm-delete-btn"
                      title="Delete row"
                      onClick={() => onDeleteRow(i)}
                    >
                      <HiOutlineTrash />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="dm-empty-row">No rows match your search.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default MappingTable;
